/**
 * Kind 카드 그리드 — Catalog view 의 첫 번째 섹션.
 *
 * 각 카드는:
 * - icon (없으면 displayName 첫 글자) · displayName · name(코드) · description
 * - color 스와치 (없으면 KIND_COLORS fallback)
 * - "N used" 사용량 배지 (usage 가 주어진 경우)
 * - 삭제 버튼 (카드 우상단)
 *
 * 마지막 카드는 `+ Add Kind` (점선 테두리) — onAdd 콜백.
 */
import type { KindDef } from "../../api/admin-client.ts"
import { KIND_COLORS } from "../graph/graph-colors.ts"

interface KindCardGridProps {
	readonly kinds: ReadonlyArray<KindDef>
	readonly usage?: Record<string, number>
	readonly selectedName?: string
	readonly onSelect: (name: string) => void
	readonly onAdd: () => void
	readonly onDelete: (name: string) => void
}

/** kind.color → KIND_COLORS[name] → KIND_COLORS.default 순으로 결정. */
function resolveColor(kind: KindDef): string {
	return kind.color ?? KIND_COLORS[kind.name] ?? KIND_COLORS.default ?? "#6e7681"
}

export function KindCardGrid(props: KindCardGridProps) {
	return (
		<section className="ontology-section">
			<h3 className="ontology-section-title">
				Kinds <span className="muted">({props.kinds.length})</span>
			</h3>
			<div className="ontology-card-grid">
				{props.kinds.map((kind) => {
					const used = props.usage?.[kind.name] ?? 0
					const isSelected = props.selectedName === kind.name
					const color = resolveColor(kind)
					return (
						<button
							type="button"
							key={kind.name}
							className={`ontology-card${isSelected ? " is-selected" : ""}`}
							onClick={() => props.onSelect(kind.name)}
						>
							<div className="ontology-card-head">
								<span className="ontology-card-icon" style={{ background: color, color: "#09090b" }}>
									{kind.icon ?? (kind.displayName || kind.name).charAt(0).toUpperCase()}
								</span>
								<div className="ontology-card-title">
									<strong>{kind.displayName || kind.name}</strong>
									<code className="inline">{kind.name}</code>
								</div>
								<button
									type="button"
									className="ontology-card-remove"
									aria-label={`${kind.name} 삭제`}
									onClick={(e) => {
										e.stopPropagation()
										props.onDelete(kind.name)
									}}
								>
									×
								</button>
							</div>
							{kind.description && <p className="ontology-card-desc">{kind.description}</p>}
							<div className="ontology-card-foot">
								{kind.category && <span className="chip">{kind.category}</span>}
								<span className={`badge${used > 0 ? " badge-active" : " badge-disabled"}`}>
									{used}회 사용
								</span>
							</div>
						</button>
					)
				})}

				<button type="button" className="ontology-card ontology-card-add" onClick={props.onAdd}>
					<span className="ontology-card-add-plus" aria-hidden>
						+
					</span>
					<span>Kind 추가</span>
				</button>
			</div>
		</section>
	)
}
